import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { walletAPI, memberAPI, tournamentAPI } from '../services/api';
import './WalletCreate.css';

function WalletCreate() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [tournaments, setTournaments] = useState([]);
  const [formData, setFormData] = useState({
    ownerType: 'member',
    ownerId: '',
    ownerName: '',
    initialBalance: 0,
    notes: ''
  });
  const [memberSearch, setMemberSearch] = useState('');
  const [showMemberDropdown, setShowMemberDropdown] = useState(false);
  const [filteredMembers, setFilteredMembers] = useState([]);
  const [searchLoading, setSearchLoading] = useState(false);

  useEffect(() => {
    loadTournaments();
  }, []);

  // Debounce search to avoid too many API calls
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      if (memberSearch) {
        searchMembers(memberSearch);
      }
    }, 300);

    return () => clearTimeout(timeoutId);
  }, [memberSearch]);

  const loadTournaments = async () => {
    try {
      const response = await tournamentAPI.getAll();
      setTournaments(response.data || []);
    } catch (error) {
      console.error('Error loading tournaments:', error);
    }
  };

  const searchMembers = async (searchValue) => {
    if (searchValue.length < 2) {
      setFilteredMembers([]);
      setShowMemberDropdown(false);
      return;
    }

    try {
      setSearchLoading(true);
      const response = await memberAPI.getAll({ 
        search: searchValue, 
        limit: 15 
      });
      const members = response.data?.data?.members || [];
      setFilteredMembers(members);
      setShowMemberDropdown(members.length > 0);
    } catch (error) {
      console.error('Error searching members:', error);
      setFilteredMembers([]);
      setShowMemberDropdown(false);
    } finally {
      setSearchLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      if (formData.ownerType === 'club') {
        // Validate club name is provided
        if (!formData.ownerName.trim()) {
          alert('Club name is required');
          return;
        }
        
        // Use specific club wallet API
        await walletAPI.createClubWallet(
          formData.ownerName,
          parseFloat(formData.initialBalance) || 0,
          formData.notes
        );
      } else {
        // Use general wallet API for member/tournament wallets
        const walletData = {
          ownerId: formData.ownerId,
          ownerType: formData.ownerType,
          ownerName: formData.ownerName,
          initialBalance: parseFloat(formData.initialBalance) || 0,
          notes: formData.notes
        };

        await walletAPI.create(walletData);
      }
      
      navigate('/wallets');
    } catch (error) {
      alert(error.response?.data?.message || 'Failed to create wallet');
    } finally {
      setLoading(false);
    }
  };

  const handleMemberSearchChange = (e) => {
    const searchValue = e.target.value;
    setMemberSearch(searchValue);
    
    if (searchValue.length === 0) {
      setFilteredMembers([]);
      setShowMemberDropdown(false);
    }
  };

  const handleMemberSelect = (member) => {
    setMemberSearch(`${member.firstName} ${member.lastName} - ${member.ghin}`);
    setFormData(prev => ({
      ...prev,
      ownerId: member._id,
      ownerName: `${member.firstName} ${member.lastName}`
    }));
    setShowMemberDropdown(false);
  };

  const handleMemberInputBlur = () => {
    // Delay hiding dropdown to allow for click
    setTimeout(() => setShowMemberDropdown(false), 200);
  };

  const handleMemberInputFocus = () => {
    if (memberSearch.length > 0) {
      setShowMemberDropdown(true);
    }
  };

  const handleTournamentChange = (e) => {
    const tournamentId = e.target.value;
    const tournament = tournaments.find(t => t._id === tournamentId);
    
    setFormData(prev => ({
      ...prev,
      ownerId: tournamentId,
      ownerName: tournament ? tournament.name : ''
    }));
  };

  return (
    <div className="wallet-create">
      <header className="page-header">
        <h1>Create New Wallet</h1>
        <button 
          onClick={() => navigate('/wallets')} 
          className="back-button"
        >
          ← Back to Wallets
        </button>
      </header>

      <div className="create-form-container">
        <form onSubmit={handleSubmit} className="wallet-create-form">
          <div className="form-group">
            <label htmlFor="ownerType">Wallet Type:</label>
            <select 
              id="ownerType"
              value={formData.ownerType}
              onChange={(e) => setFormData(prev => ({...prev, ownerType: e.target.value}))}
              required
            >
              <option value="member">Member Wallet</option>
              <option value="tournament">Tournament Wallet</option>
              <option value="club">Club Wallet</option>
            </select>
          </div>

          {formData.ownerType === 'member' && (
            <div className="form-group">
              <label htmlFor="member">Search Member:</label>
              <div className="typeahead-container">
                <input
                  type="text"
                  id="member"
                  value={memberSearch}
                  onChange={handleMemberSearchChange}
                  onFocus={handleMemberInputFocus}
                  onBlur={handleMemberInputBlur}
                  placeholder={searchLoading ? "Searching..." : "Type member name or GHIN number..."}
                  required
                />
                {showMemberDropdown && filteredMembers.length > 0 && (
                  <div className="typeahead-dropdown">
                    {filteredMembers.map(member => (
                      <div
                        key={member._id}
                        className="typeahead-option"
                        onClick={() => handleMemberSelect(member)}
                      >
                        <div className="member-name">
                          {member.firstName} {member.lastName}
                        </div>
                        <div className="member-ghin">GHIN: {member.ghin}</div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}

          {formData.ownerType === 'tournament' && (
            <div className="form-group">
              <label htmlFor="tournament">Select Tournament:</label>
              <select 
                id="tournament"
                value={formData.ownerId}
                onChange={handleTournamentChange}
                required
              >
                <option value="">Choose a tournament...</option>
                {tournaments.map(tournament => (
                  <option key={tournament._id} value={tournament._id}>
                    {tournament.name} - {tournament.date ? new Date(tournament.date).toLocaleDateString() : 'No date'}
                  </option>
                ))}
              </select>
            </div>
          )}

          {formData.ownerType === 'club' && (
            <div className="form-group">
              <label htmlFor="clubName">Club Name:</label>
              <input
                type="text"
                id="clubName"
                value={formData.ownerName}
                onChange={(e) => setFormData(prev => ({...prev, ownerName: e.target.value}))}
                placeholder="Enter club name..."
                required
              />
            </div>
          )}

          <div className="form-group">
            <label htmlFor="initialBalance">Initial Balance:</label>
            <input
              type="number"
              id="initialBalance"
              value={formData.initialBalance}
              onChange={(e) => setFormData(prev => ({...prev, initialBalance: e.target.value}))}
              min="0"
              step="0.01"
              placeholder="0.00"
            />
          </div>

          <div className="form-group">
            <label htmlFor="notes">Notes (optional):</label>
            <textarea
              id="notes"
              value={formData.notes}
              onChange={(e) => setFormData(prev => ({...prev, notes: e.target.value}))}
              placeholder="Optional notes about this wallet"
              rows="3"
            />
          </div>

          <div className="form-actions">
            <button 
              type="button" 
              onClick={() => navigate('/wallets')}
              className="cancel-button"
            >
              Cancel
            </button>
            <button 
              type="submit" 
              disabled={loading}
              className="create-button"
            >
              {loading ? 'Creating...' : 'Create Wallet'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default WalletCreate;